import { useMemo, useRef, useState } from "react";
import { DocHeader } from "@/components/DocHeader";
import { TrendingUp, CalendarDays, CalendarRange, Calendar, Sigma, Lock } from "lucide-react";
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useApp } from "@/context/AppContext";
import { fmtNum } from "@/lib/format";
import { profitSeries, profitSummary } from "@/lib/profit";
import { Chip } from "@/components/ui-kit";
import { ReportActions } from "@/components/tpv/ZReport";
import { exportCsv } from "@/components/DataGrid";

const CARDS = [["day", CalendarDays, "np_today"], ["week", CalendarRange, "np_week"], ["month", Calendar, "np_month"], ["total", Sigma, "np_total"]];

function Card({ icon: Icon, label, r, money, t, testid }) {
  const neg = r.net < 0;
  return (
    <div className={`rounded-xl border p-4 bg-white relative overflow-hidden ${neg ? "border-[#E74C3C]/40" : "border-[#1F9D55]/40"}`} data-testid={testid}>
      <Icon className="absolute -end-3 -top-3 w-20 h-20 opacity-[.06]" />
      <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Icon className="w-4 h-4" />{label}</div>
      <div className={`text-3xl font-black mt-1 tabular-nums ${neg ? "text-[#C7353B]" : "text-[#1F9D55]"}`} dir="ltr" data-testid={`${testid}-net`}>{money(r.net)}</div>
      <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3" dir="ltr"><span>{t("revenue")}: {fmtNum(r.revenue)}</span><span>{t("np_margin")}: {r.margin}%</span><span>{r.count} {t("tickets")}</span></div>
    </div>
  );
}

export default function NetProfit() {
  const { t, view, money, staff } = useApp();
  const [mode, setMode] = useState("day");
  const ref = useRef(null);
  const sum = useMemo(() => profitSummary(view), [view]);
  const series = useMemo(() => profitSeries(view, mode), [view, mode]);
  if (staff?.role !== "owner") return <div className="rounded-xl bg-secondary p-6 text-center text-sm flex items-center justify-center gap-2" data-testid="np-owner-only"><Lock className="w-4 h-4" />{t("owner_only")}</div>;
  const cols = [{ key: "label", label: t("np_period") }, { key: "count", label: t("tickets") }, { key: "revenue", label: t("revenue") }, { key: "returns", label: t("returns") }, { key: "cogs", label: t("cogs") }, { key: "gross", label: t("gross_profit") }, { key: "house_cost", label: t("house_cost") }, { key: "expenses", label: t("expenses") }, { key: "waste", label: t("waste_losses") }, { key: "cash_diff", label: t("fin_cash_diff") }, { key: "net", label: t("net_profit") }, { key: "margin", label: t("np_margin") }];
  const rows = [...series].reverse();
  return (
    <div className="space-y-4" data-testid="net-profit">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{CARDS.map(([k, Icon, l]) => <Card key={k} icon={Icon} label={t(l)} r={sum[k]} money={money} t={t} testid={`np-card-${k}`} />)}</div>
      <div ref={ref} className="print-area rounded-xl border border-border bg-white p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <DocHeader title={t("np_title")} /><span className="me-auto" />
          <div className="flex gap-1 no-print">{["day", "week", "month"].map((m) => <Chip key={m} active={mode === m} onClick={() => setMode(m)} data-testid={`np-mode-${m}`}>{t(`np_by_${m}`)}</Chip>)}</div>
        </div>
        <div className="h-56" data-testid="np-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={series} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" /><YAxis tick={{ fontSize: 10 }} width={56} tickFormatter={(v) => fmtNum(v)} />
              <Tooltip formatter={(v) => money(v)} labelStyle={{ fontWeight: 700 }} />
              <Bar dataKey="net" name={t("net_profit")} radius={[4, 4, 0, 0]}>{series.map((e, i) => <Cell key={i} fill={e.net < 0 ? "#E74C3C" : "#1F9D55"} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="overflow-auto max-h-[46vh] rounded-lg border border-[#CCCCCC]">
          <table className="w-full text-[13px]" data-testid="np-table">
            <thead><tr className="bg-[#E8E8E8] text-xs">{cols.map((c) => <th key={c.key} className={`py-2 px-3 whitespace-nowrap ${c.key === "label" ? "text-start" : "text-end"}`}>{c.label}</th>)}</tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.label} className="border-t border-[#E5E5E5] hover:bg-[#EAF2FC]" data-testid="np-row">
                <td className="px-3 py-1.5 font-bold" dir="ltr">{r.label}</td><td className="px-3 text-end">{r.count}</td>
                {["revenue", "returns", "cogs", "gross", "house_cost", "expenses", "waste", "cash_diff"].map((k) => <td key={k} className="px-3 text-end tabular-nums" dir="ltr">{fmtNum(r[k])}</td>)}
                <td className={`px-3 text-end font-extrabold tabular-nums ${r.net < 0 ? "text-[#C7353B]" : "text-[#1F9D55]"}`} dir="ltr">{fmtNum(r.net)}</td><td className="px-3 text-end" dir="ltr">{r.margin}%</td>
              </tr>))}</tbody>
          </table>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <ReportActions target={ref} name={`net-profit-${mode}`} wide />
        <button type="button" onClick={() => exportCsv(`net-profit-${mode}`, cols, rows)} className="h-10 px-3 rounded border border-border bg-white text-sm font-bold" data-testid="np-excel">Excel</button>
      </div>
    </div>
  );
}
